// @file Shared utils for whiteboard previews (frame and object previews), used in whiteboard_object_preview.ts and whiteboard_frame_preview.ts
import { CANVAS_WIDTH } from '@@/bits/whiteboard_no_dependencies'
import { CANVAS_CARD_PREVIEW_PADDING } from '@@/pinia/whiteboard_frame_preview'
import { useTldrawEditor } from '@@/vuecomposables/useTldrawEditor'
import { flattenDeep } from 'es-toolkit'
import { debounce } from 'es-toolkit/compat'
import type { HistoryEntry, TLImageExportOptions, TLRecord, TLShapeId } from 'tldraw'

// x2 for retina
export const FRAME_PREVIEW_IMAGE_WIDTH = 96 * 2
export const FRAME_PREVIEW_IMAGE_HEIGHT = 54 * 2
export const FRAME_PREVIEW_IMAGE_WIDTH_DESKTOP = 69 * 2
export const FRAME_PREVIEW_IMAGE_HEIGHT_DESKTOP = 40 * 2

export const FRAME_PREVIEW_SCALE = (): number =>
  FRAME_PREVIEW_IMAGE_WIDTH / (CANVAS_WIDTH + CANVAS_CARD_PREVIEW_PADDING * 2)

export function createDebouncedHandlePageChange(
  handler: (event: HistoryEntry<TLRecord>) => void,
): (event: HistoryEntry<TLRecord>) => void {
  return debounce(handler, 1000, { maxWait: 1000, trailing: true })
}

/**
 * Get the changed ids from the store update event, used to determine the list of objects that have changed
 */
export function getChangedIds(event: HistoryEntry<TLRecord>, changeObjectType: 'shape' | 'page'): Set<string> {
  const changeIds = new Set<string>()
  flattenDeep([
    Object.values(event.changes.added),
    Object.values(event.changes.updated),
    Object.values(event.changes.removed),
  ])
    .map((change) => {
      if (changeObjectType === 'shape' && change.typeName === 'shape') {
        return change.id
      }
      if (changeObjectType === 'page') {
        if (change.typeName === 'page') {
          return change.id
        }
        if (change.typeName === 'shape') {
          const { editor } = useTldrawEditor()
          return editor?.getAncestorPageId(change.id) ?? null
        }
      }
      return null
    })
    .filter((id) => id != null)
    .forEach((id) => changeIds.add(id as string))
  return changeIds
}

/**
 * Check if the editor is idle, this is used to prevent unnecessary updates to the previews when the editor is not idle (user is still drawing, etc.)
 * When user is drawing, updating the previews will make the sandbox super laggy and could be unusable.
 * @returns
 */
export function isEditorIdle(): boolean {
  const { editor } = useTldrawEditor()
  return (
    editor?.isInAny(
      'draw.idle',
      'select.idle',
      'highlight.idle',
      'text.idle',
      'padlet-draw-eraser.idle',
      'laser.idle',
      'note.idle',
      'geo.idle',
      'interact',
    ) ?? false
  )
}

/**
 * Get the svg string for the shapes, used to generate the preview for the objects
 */
export async function getSvgStringForShapes(
  shapeIds: TLShapeId[],
  options: TLImageExportOptions = {},
): Promise<string | null> {
  const { editor } = useTldrawEditor()
  if (editor == null) {
    return null
  }
  const svgData = await editor.getSvgString(shapeIds, {
    scale: FRAME_PREVIEW_SCALE(), // load a smaller image so that it loads faster
    background: false,
    ...options,
  })
  return svgData?.svg ?? null
}

/**
 * Get the minified svg string for the shapes, used to generate PNG previews for the objects
 */
export async function getMinifiedSvgStringForShapes(
  shapeIds: TLShapeId[],
  options: TLImageExportOptions = {},
): Promise<string | null> {
  const { editor } = useTldrawEditor()
  if (editor == null) {
    return null
  }
  const svgData = await editor.getSvgElement(shapeIds, {
    scale: FRAME_PREVIEW_SCALE(), // load a smaller image so that it loads faster
    background: false,
    ...options,
  })
  // Return the svg string, but remove the inner style tag
  const svgElement = svgData?.svg
  svgElement?.querySelector('style')?.remove()
  svgElement?.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
  return svgElement?.outerHTML ?? null
}
